import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { GATHERING_LIMITS, gatheringContentSchema } from '@twinden/shared';
import { defaultRings } from '../../lib/gatherings';
import type { GatheringDraft } from '../../store';
import { useT } from '../../i18n';

// "+ → Gathering" (M71, D282): what, when, where, and whether to ring people
// when it's time. It says up front that replies are seen by the whole chat.

type Day = 'today' | 'tomorrow' | 'pick';
const DAYS: Day[] = ['today', 'tomorrow', 'pick'];

const two = (n: number) => String(n).padStart(2, '0');
const dateValue = (d: Date) =>
  `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;

/** The next whole hour, as "HH:MM": a time most dinners can start from. */
export function nextHour(now: number): { date: string; time: string } {
  const at = new Date(now);
  at.setHours(at.getHours() + 1, 0, 0, 0);
  return { date: dateValue(at), time: `${two(at.getHours())}:00` };
}

/**
 * When the chosen day and time fall, on this device's clock, or null while
 * either is empty. Exported for the tests.
 */
export function gatheringStart(
  day: Day,
  pickedDate: string,
  time: string,
  now: number
): Date | null {
  if (!time) return null;
  const base = new Date(now);
  if (day === 'tomorrow') base.setDate(base.getDate() + 1);
  const date = day === 'pick' ? pickedDate : dateValue(base);
  if (!date) return null;
  const at = new Date(`${date}T${time}`);
  return Number.isNaN(at.getTime()) ? null : at;
}

export function GatheringSheet({
  isOpen,
  onClose,
  onSend,
  participantCount,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSend: (title: string, gathering: GatheringDraft) => void;
  /** Everyone in the chat, you included: decides if rings start on (D286). */
  participantCount: number;
}) {
  const t = useT();
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const [title, setTitle] = useState('');
  const [day, setDay] = useState<Day>('today');
  const [pickedDate, setPickedDate] = useState('');
  const [time, setTime] = useState('');
  const [place, setPlace] = useState('');
  const [note, setNote] = useState('');
  const [rings, setRings] = useState(true);
  const [opened, setOpened] = useState(() => Date.now());
  // When the time was last checked: on opening, then again at Send, since a
  // sheet left open for a while can hold a time that has passed since.
  const [checkedAt, setCheckedAt] = useState(() => Date.now());

  // A fresh gathering each time the sheet opens.
  useEffect(() => {
    if (!isOpen) return;
    const now = Date.now();
    const start = nextHour(now);
    setOpened(now);
    setCheckedAt(now);
    setTitle('');
    // After 11 pm the next whole hour is tomorrow's midnight.
    setDay(start.date === dateValue(new Date(now)) ? 'today' : 'tomorrow');
    setPickedDate(start.date);
    setTime(start.time);
    setPlace('');
    setNote('');
    setRings(defaultRings(participantCount));
  }, [isOpen, participantCount]);

  // D282: these seven, in this order.
  const quick = [
    t('chat.gathering.quickBreakfast'),
    t('chat.gathering.quickLunch'),
    t('chat.gathering.quickDinner'),
    t('chat.gathering.quickChai'),
    t('chat.gathering.quickMovie'),
    t('chat.gathering.quickOuting'),
    t('chat.gathering.quickMeetUp'),
  ];

  const words = {
    title: title.trim(),
    place: place.trim() || undefined,
    note: note.trim() || undefined,
  };
  const start = gatheringStart(day, pickedDate, time, opened);
  const startMs = start?.getTime() ?? NaN;
  // The server takes a start a few minutes back and up to 60 days ahead.
  const timeFits = (at: number) =>
    !Number.isNaN(startMs) &&
    startMs >= at - GATHERING_LIMITS.PAST_GRACE_MS &&
    startMs <= at + GATHERING_LIMITS.MAX_AHEAD_MS;
  const timeOk = timeFits(checkedAt);
  const valid = gatheringContentSchema.safeParse(words).success && timeOk;

  const send = () => {
    if (!valid || !start) return;
    const sendAt = Date.now();
    if (!timeFits(sendAt)) {
      // Shows the time error instead of a send the server would refuse.
      setCheckedAt(sendAt);
      return;
    }
    onSend(words.title, {
      ...(words.place ? { place: words.place } : {}),
      ...(words.note ? { note: words.note } : {}),
      startsAt: start.toISOString(),
      rings,
    });
  };

  const content = (
    <Stack spacing={4}>
      <Heading as="h2" fontSize="24px" fontWeight={700}>
        {t('chat.gathering.title')}
      </Heading>
      <FormControl>
        <FormLabel fontWeight={700}>{t('chat.gathering.titleLabel')}</FormLabel>
        <HStack spacing={2} flexWrap="wrap" pb={2}>
          {quick.map((word) => (
            <Button
              key={word}
              size="sm"
              borderRadius="full"
              variant={title === word ? 'solid' : 'outline'}
              colorScheme={title === word ? 'brand' : 'gray'}
              aria-pressed={title === word}
              onClick={() => setTitle(word)}
            >
              {word}
            </Button>
          ))}
        </HStack>
        <Input
          value={title}
          maxLength={GATHERING_LIMITS.TITLE_LENGTH}
          placeholder={t('chat.gathering.titlePlaceholder')}
          onChange={(event) => setTitle(event.target.value)}
          autoFocus
        />
      </FormControl>
      <Box>
        <Text fontWeight={700} mb={2}>
          {t('chat.gathering.day')}
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          {DAYS.map((choice) => (
            <Button
              key={choice}
              size="sm"
              borderRadius="full"
              variant={day === choice ? 'solid' : 'outline'}
              colorScheme={day === choice ? 'brand' : 'gray'}
              aria-pressed={day === choice}
              onClick={() => setDay(choice)}
            >
              {t(
                choice === 'today'
                  ? 'chat.gathering.dayToday'
                  : choice === 'tomorrow'
                    ? 'chat.gathering.dayTomorrow'
                    : 'chat.gathering.dayPick'
              )}
            </Button>
          ))}
        </HStack>
        {day === 'pick' && (
          <Input
            mt={2}
            type="date"
            aria-label={t('chat.gathering.dayPick')}
            value={pickedDate}
            min={dateValue(new Date(opened))}
            max={dateValue(new Date(opened + GATHERING_LIMITS.MAX_AHEAD_MS))}
            onChange={(event) => setPickedDate(event.target.value)}
          />
        )}
      </Box>
      <FormControl isInvalid={Boolean(time) && !timeOk}>
        <FormLabel fontWeight={700}>{t('chat.gathering.time')}</FormLabel>
        <Input
          type="time"
          value={time}
          onChange={(event) => setTime(event.target.value)}
        />
        {Boolean(time) && !timeOk && (
          <Text role="alert" pt={1} fontSize="13px" color="danger">
            {t('chat.gathering.timeError')}
          </Text>
        )}
      </FormControl>
      <FormControl>
        <FormLabel fontWeight={700}>{t('chat.gathering.place')}</FormLabel>
        <Input
          value={place}
          maxLength={GATHERING_LIMITS.PLACE_LENGTH}
          placeholder={t('chat.gathering.placePlaceholder')}
          onChange={(event) => setPlace(event.target.value)}
        />
      </FormControl>
      <FormControl>
        <FormLabel fontWeight={700}>{t('chat.gathering.note')}</FormLabel>
        <Input
          value={note}
          maxLength={GATHERING_LIMITS.NOTE_LENGTH}
          placeholder={t('chat.gathering.notePlaceholder')}
          onChange={(event) => setNote(event.target.value)}
        />
      </FormControl>
      <FormControl display="flex" alignItems="center" gap={3}>
        <Switch
          id="gathering-rings"
          isChecked={rings}
          onChange={(event) => setRings(event.target.checked)}
        />
        <FormLabel htmlFor="gathering-rings" mb={0} fontWeight={600}>
          {t('chat.gathering.rings')}
        </FormLabel>
      </FormControl>
      <Text fontSize="13px" color="muted" mt="-8px">
        {t('chat.gathering.ringsHint')}
      </Text>
      <Text fontSize="13px" color="muted">
        {t('chat.gathering.visible')}
      </Text>
      <HStack spacing={3}>
        <Button
          flex="1"
          h="52px"
          variant="outline"
          colorScheme="gray"
          borderColor="line"
          onClick={onClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          flex="1"
          h="52px"
          colorScheme="brand"
          isDisabled={!valid}
          onClick={send}
        >
          {t('chat.gathering.create')}
        </Button>
      </HStack>
    </Stack>
  );

  if (phone) {
    return (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          aria-label={t('chat.gathering.title')}
          maxH="92dvh"
          pb="max(env(safe-area-inset-bottom), 20px)"
        >
          <DrawerBody px={5} pt="10px" pb={0} overflowY="auto">
            <Box
              w="40px"
              h="5px"
              borderRadius="3px"
              bg="line"
              mx="auto"
              mb={4}
              aria-hidden="true"
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent
        mx={5}
        borderRadius="28px"
        bg="surface"
        aria-label={t('chat.gathering.title')}
        maxH="92dvh"
        overflowY="auto"
      >
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
